/**
 * 작성자 : 홍선영
 * 날짜 : 2025.01.07
 * 경로 : 설정관리-환경설정-일반관리탭 (사내)
 */

import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useRecoilState } from 'recoil';
import styled from 'styled-components';

import { userState } from '../../../atoms';
import { SCREEN_COLOR_MODE } from '../../../_constants';
import { BtnBlue } from '../../../components/Button';
import SelectBox from '../../../components/SelectBox';
import { logPost } from '../../../services/log';
import { useSetAuth } from '../../../utils/useSetAuth';
import { useTranslation } from 'react-i18next';
import { ContentsContainerRoot } from '../../../assets/styles/ContentsContainerRoot';
import { apiPost } from '../../../services/_common';
import { LoadingModalBackground } from '../../../assets/styles/Modal';
import { PulseLoader } from 'react-spinners';
import IssueGuide from '../../../components/IssueGuide';
import GetFetchStatus from '../../../hooks/GetFetchStatus';

const ButtonsWrapper = styled.div`
  border-top: 1px solid var(--outline);
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem;
  width: 100%;
  justify-content: flex-end;
  button {
    height: 2.5rem;
    font-size: 0.875rem;
  }
`;

const Root = styled(ContentsContainerRoot)`
  align-items: center;
  .inputForm {
    overflow: auto;
    flex-grow: 1;
    position: relative;
    width: calc(100% - 0.5rem);
    margin-left: 0.5rem;
    > div {
      width: 100%;
    }
    .setting-container-group {
      width: 100%;
      max-width: 40rem;
      margin: 0 auto;
      gap: 2rem;
      display: flex;
      flex-direction: column;
      padding-bottom: 4rem;
    }
    .title {
      border-bottom: 1px solid var(--outline);
      color: var(--text-primary);
      height: 3rem;
      display: flex;
      align-items: center;
      padding: 0 0.5rem;
    }
    .setting-grid {
      display: grid;
      grid-template-columns: repeat(1, 1fr);
      column-gap: 2rem;
      row-gap: 1rem;
      padding: 1rem 0.5rem;
      @media (min-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
      }
    }
    .setting-row {
      display: flex;
      flex-direction: row;
      align-items: center;
      height: 2.5rem;
      input {
        font-size: 0.875rem;
      }
      @media (min-width: 768px) {
      }
      label {
        font-weight: 500;
        font-size: 0.875rem;
        width: 6rem;
        flex-shrink: 0;
        line-height: 1.4;
      }
      > div {
        flex-grow: 1;
        ul li {
          max-width: 100%;
          width: 100%;
          span {
            width: 100%;
          }
        }
      }
      .setting-row-content {
        display: flex;
        align-items: center;
        > span {
          padding: 0 0.5rem;
          font-size: 0.875rem;
          color: var(--text-secondary);
        }
      }
    }
  }
`;

const CGeneralSetting = ({ mqtt }: any) => {
  const { t } = useTranslation();
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const [userInfo, setUserInfo] = useRecoilState(userState);
  const [bScreenMode, setBScreenMode] = useState({ type: 'bScreenMode', bScreenMode: '', cdName: '' });
  const { data: companySetData, isLoading, isFetching, isRefetching, isError } = GetFetchStatus({ path: '/company/set', hCdOnly: { value: true, key: 'cCd' } });

  useEffect(() => {
    if (companySetData) {
      const mode = companySetData.companySet?.bScreenMode;
      setBScreenMode({ type: 'bScreenMode', bScreenMode: mode, cdName: mode === '1' ? 'LIGHT' : 'DARK' });
    }
  }, [companySetData]);

  useEffect(() => {
    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: '환경 설정 > 일반 관리 (사내)',
      action: '조회',
      etc: ``,
    });
  }, []);

  const saveGeneralSettingAPI = async () => {
    const req = {
      cCd: userInfo.hCd,
      bScreenMode: bScreenMode.bScreenMode,
    };
    const res = await apiPost({ path: '/company/set', req });
    const { statusCode, message, data } = res.data;
    if (statusCode === 200) {
      setUserInfo((prev) => ({ ...prev, ...data.generalSet }));
      toast.success(t(message));
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '환경 설정 > 일반 관리',
        action: '저장',
        etc: ``,
      });
      mqtt.mqtt.publish(`${userInfo.hCd}${userInfo.sCd}/board`, 'test');
    }
  };

  const onClickSave = () => {
    return saveGeneralSettingAPI();
  };

  if (isError) return <IssueGuide text='요청하신 작업을 완수하지 못했습니다 잠시 후 다시 시도하세요' type='error' />;
  if (isLoading || isFetching || isRefetching)
    return (
      <LoadingModalBackground>
        <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' style={{ width: 'inherit', height: 'inherit' }} />
      </LoadingModalBackground>
    );
  if (!companySetData) return <IssueGuide />;

  return (
    <Root loading={false}>
      <div className='inputForm'>
        <div className='setting-container-group'>
          <div className='setting-container'>
            <div className='title'>{t('상황판 설정')}</div>
            <div className='setting-grid'>
              <div className='setting-row'>
                <label htmlFor='pwdReuseTerm'>{t('화면 모드')}</label>
                <SelectBox
                  options={SCREEN_COLOR_MODE}
                  defaultOption={companySetData.companySet?.bScreenMode === '1' ? 'LIGHT' : 'DARK'}
                  state={bScreenMode}
                  setState={setBScreenMode}
                  stateKey='bScreenMode'
                  grantChk={!auth.createAuth && !auth.updateAuth}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <ButtonsWrapper>{(auth.createAuth || auth.updateAuth) && <BtnBlue onClick={onClickSave}>{t('저장')}</BtnBlue>}</ButtonsWrapper>
    </Root>
  );
};

export default CGeneralSetting;
